import React from 'react';
import { useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { Button, Form, Input, message, Radio, Space, Spin } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Link } from 'react-router-dom';
import styles from '../index.module.css';
import { editUserRequest, getUsers } from '../../../store/actions/users';
import { useTranslation } from 'react-i18next';

const UsersEdit = () => {
  const { id } = useParams();
  const { t } = useTranslation();

  const dispatch = useDispatch();
  const history = useHistory();

  const usersSelect = useSelector((state) =>
    state.users.users.find((user) => user.id === id)
  );

  const isLoading = useSelector((state) => state.users.loading);

  useEffect(() => {
    if (!usersSelect) {
      dispatch(getUsers());
    }
  }, [dispatch, usersSelect]);

  const [languageValue, setLanguageValue] = useState('fr');
  const [validatedByGuestValue, setValidatedByGuestValue] = useState(false);
  const [validatedBySponsorValue, setValidatedBySponsorValue] = useState(false);

  const onChangeLanguage = (e) => {
    setLanguageValue(e.target.value);
  };

  const onChangeValidatedByGuest = (e) => {
    setValidatedByGuestValue(e.target.value);
  };

  const onChangeValidatedBySponsor = (e) => {
    setValidatedBySponsorValue(e.target.value);
  };

  const handleFinish = (values) => {
    dispatch(
      editUserRequest({
        id: id,
        body: {
          ...usersSelect,
          ...values,
        },
        redirection: () => {
          history.push('/admin/users');
        },
      })
    );
  };

  useEffect(() => {
    const timeout = setTimeout(() => {
      history.push('/admin/users');
      message.error('Utilisateur introuvable');
    }, 3000);

    if (usersSelect) {
      clearTimeout(timeout);
    }

    return () => {
      clearTimeout(timeout);
    };
  }, [history, usersSelect]);

  if (isLoading) {
    return (
      <Spin
        size='large'
        style={{
          position: 'absolute',
          left: '50%',
          top: '50%',
          transform: 'translate(-50%,-50%)',
        }}
      />
    );
  }

  return (
    <div
      className={
        styles['admin-content'] +
        ' ' +
        styles['network-content'] +
        ' ' +
        styles['network-content']
      }
    >
      <div className={styles.table__header}>
        <h3 className={styles.table__title}>Modifier un utilisateur</h3>
      </div>
      <div className={styles.form__container}>
        {usersSelect ? (
          <Form
            layout='vertical'
            name='network-edit'
            onFinish={handleFinish}
            initialValues={{
              ...usersSelect,
              startAt: usersSelect.startAt.split('T')[0],
              endAt: usersSelect.endAt.split('T')[0],
              language: usersSelect.language || languageValue,
              validatedByGuest:
                usersSelect.validatedByGuest || validatedByGuestValue,
              validatedBySponsor:
                usersSelect.validatedBySponsor || validatedBySponsorValue,
            }}
          >
            <div className={styles.form__group}>
              <Form.Item
                className={styles.form__group__item}
                name='lastName'
                label={t('admin.users.edit.label.lastName')}
                rules={[
                  {
                    required: true,
                    message: t('admin.users.edit.validation.lastName'),
                  },
                ]}
              >
                <Input
                  placeholder={t('admin.users.edit.placeholder.lastName')}
                />
              </Form.Item>
              <Form.Item
                className={styles.form__group__item}
                name='firstName'
                label={t('admin.users.edit.label.firstName')}
                rules={[
                  {
                    required: true,
                    message: t('admin.users.edit.validation.firstName'),
                  },
                ]}
              >
                <Input
                  placeholder={t('admin.users.edit.placeholder.firstName')}
                />
              </Form.Item>
            </div>

            <Form.Item
              name='guest'
              label={t('admin.users.edit.label.guest')}
              rules={[
                {
                  required: true,
                  message: t('admin.users.edit.validation.guest.required'),
                },
                {
                  type: 'email',
                  message: t('admin.users.edit.validation.guest.email'),
                },
              ]}
            >
              <Input placeholder={t('admin.users.edit.placeholder.guest')} />
            </Form.Item>
            <Form.Item
              name='company'
              label={t('admin.users.edit.label.company')}
              rules={[
                {
                  required: true,
                  message: t('admin.users.edit.validation.company'),
                },
              ]}
            >
              <Input placeholder={t('admin.users.edit.placeholder.company')} />
            </Form.Item>
            <Form.Item
              name='sponsor'
              label={t('admin.users.edit.label.sponsor')}
              rules={[
                {
                  required: true,
                  message: t('admin.users.edit.validation.sponsor.required'),
                },
                {
                  type: 'email',
                  message: t('admin.users.edit.validation.sponsor.email'),
                },
              ]}
            >
              <Input placeholder={t('admin.users.edit.placeholder.sponsor')} />
            </Form.Item>
            <div
              className={styles.form__group}
              style={{
                display: 'flex',
                gap: '3rem',
                justifyContent: 'space-between',
              }}
            >
              <Form.Item
                className={styles.form__group__item}
                style={{ width: '100%' }}
                name='startAt'
                label={t('admin.users.edit.label.startAt')}
                rules={[
                  {
                    required: true,
                    message: t('admin.users.edit.validation.startAt.required'),
                  },
                  {
                    type: 'date',
                    message: t('admin.users.edit.validation.startAt.date'),
                  },
                ]}
              >
                <Input type='date' />
              </Form.Item>
              <Form.Item
                className={styles.form__group__item}
                style={{ width: '100%' }}
                name='endAt'
                label={t('admin.users.edit.label.endAt')}
                rules={[
                  {
                    required: true,
                    message: t('admin.users.edit.validation.endAt.required'),
                  },
                  {
                    type: 'date',
                    message: t('admin.users.edit.validation.endAt.date'),
                  },

                  //Validator to check if end date is greater than start date
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      //compare start date and end date
                      if (!value || !getFieldValue('startAt')) {
                        return Promise.resolve();
                      }
                      const startDate = new Date(getFieldValue('startAt'));
                      const endDate = new Date(value);
                      if (endDate < startDate) {
                        return Promise.reject(
                          t(
                            'admin.users.edit.validation.endAt.greaterThanStart'
                          )
                        );
                      }
                      return Promise.resolve();
                    },
                  }),
                ]}
              >
                <Input type='date' />
              </Form.Item>
            </div>

            <div
              className={styles.form__group}
              style={{
                display: 'flex',
                gap: '3rem',
                justifyContent: 'space-between',
              }}
            >
              <Form.Item
                className={styles.form__group__item}
                style={{ width: '100%' }}
                name='language'
                label={t('admin.users.edit.label.language')}
                rules={[
                  {
                    required: true,
                    message: t('admin.users.edit.validation.language'),
                  },
                ]}
              >
                <Radio.Group onChange={onChangeLanguage} value={languageValue}>
                  <Space direction='vertical'>
                    <Radio value={'fr'}>
                      {t('admin.users.edit.label.french')}
                    </Radio>
                    <Radio value={'en'}>
                      {t('admin.users.edit.label.english')}
                    </Radio>
                  </Space>
                </Radio.Group>
              </Form.Item>
              <Form.Item
                className={styles.form__group__item}
                style={{ width: '100%' }}
                name='validatedByGuest'
                label={t('admin.users.edit.label.validatedByGuest')}
                rules={[
                  {
                    required: true,
                    message: t('admin.users.edit.validation.validatedByGuest'),
                  },
                ]}
              >
                <Radio.Group
                  onChange={onChangeValidatedByGuest}
                  value={validatedByGuestValue}
                >
                  <Space direction='vertical'>
                    <Radio value={true}>
                      {t('admin.users.edit.label.yes')}
                    </Radio>
                    <Radio value={false}>
                      {t('admin.users.edit.label.no')}
                    </Radio>
                  </Space>
                </Radio.Group>
              </Form.Item>
              <Form.Item
                className={styles.form__group__item}
                style={{ width: '100%' }}
                name='validatedBySponsor'
                label={t('admin.users.edit.label.validatedBySponsor')}
                rules={[
                  {
                    required: true,
                    message: t(
                      'admin.users.edit.validation.validatedBySponsor'
                    ),
                  },
                ]}
              >
                <Radio.Group
                  onChange={onChangeValidatedBySponsor}
                  value={validatedBySponsorValue}
                >
                  <Space direction='vertical'>
                    <Radio value={true}>
                      {t('admin.users.edit.label.yes')}
                    </Radio>
                    <Radio value={false}>
                      {t('admin.users.edit.label.no')}
                    </Radio>
                  </Space>
                </Radio.Group>
              </Form.Item>
            </div>

            <div className={styles.buttons__container}>
              <Link to={'/admin/users'}>
                <Button type='link' size='large'>
                  {t('admin.users.edit.button.cancel')}
                </Button>
              </Link>
              <Button
                type='primary'
                htmlType='submit'
                size='large'
                loading={isLoading ? true : false}
              >
                {t('admin.users.edit.button.submit')}
              </Button>
            </div>
          </Form>
        ) : (
          <Spin size='large' />
        )}
      </div>
    </div>
  );
};

export default UsersEdit;
